import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import {
  Check,
  Sparkles,
  RotateCcw,
  ShieldCheck,
  HeartHandshake,
  TrendingUp,
  CalendarCheck,
} from "lucide-react";
import { Breadcrumbs, breadcrumbSchema } from "@/components/Breadcrumbs";
import { Faq, faqSchema, type FaqItem } from "@/components/Faq";
import { ActionLink } from "@/components/ActionLink";
import { BRAND } from "@/data/units";
import { openTrialDialog } from "@/components/TrialDialog";
import { track } from "@/lib/tracking";
import { ImageCarousel } from "@/components/ImageCarousel";
import { AudienceCarousel } from "@/components/AudienceCarousel";


import metodo6 from "@/assets/metodo/metodo-6.jpg.asset.json";
import metodo2 from "@/assets/metodo/metodo-2.jpg.asset.json";
import metodo3 from "@/assets/metodo/metodo-3.jpg.asset.json";
import metodo4 from "@/assets/metodo/metodo-4.jpg.asset.json";
import metodo5 from "@/assets/metodo/metodo-5.jpg.asset.json";
import metodoFormato from "@/assets/metodo/metodo-formato.jpg.asset.json";
import assistido1 from "@/assets/assistido/assistido-1.jpg.asset.json";
import assistido2 from "@/assets/assistido/assistido-2.jpg.asset.json";
import assistido3 from "@/assets/assistido/assistido-3.jpg.asset.json";
import assistido4 from "@/assets/assistido/assistido-4.jpg.asset.json";
import assistido5 from "@/assets/assistido/assistido-5.jpg.asset.json";

const TITLE = "Treino personalizado e semi-privado | Método Nook";
const DESCRIPTION =
  "Entenda como funciona o treino personalizado da Nook: até três alunos por instrutor, correção técnica e acompanhamento durante toda a sessão.";

const FAQ: FaqItem[] = [
  {
    q: "O que é treino personalizado?",
    a: "É a musculação feita com um instrutor presente do começo ao fim da sessão: ele monta o treino, explica a execução, corrige a técnica e ajusta cargas enquanto você treina.",
  },
  {
    q: "Treino personalizado é o mesmo que personal?",
    a: "Não é personal individual. Na Nook o instrutor acompanha até três alunos por sessão, cada um com o seu próprio treino. Você mantém acompanhamento constante com um custo mais acessível que o personal exclusivo.",
  },
  {
    q: "O treino é individual?",
    a: "Sim. Cada aluno tem o próprio programa, adaptado ao seu momento e objetivo.",
  },
  {
    q: "Preciso já saber treinar?",
    a: "Não. O formato foi desenhado justamente para quem está começando, voltando a treinar ou quer treinar com mais segurança. A orientação é constante.",
  },
  {
    q: "Quantos alunos ficam com cada instrutor?",
    a: "No máximo três alunos por instrutor em cada sessão.",
  },
  {
    q: "Como funciona o Day Use Nook?",
    a: "O Day Use é uma experiência de R$ 100 para conhecer o método Nook na prática. Você realiza uma sessão com treino preparado e acompanhamento do instrutor. Ao contratar um plano, o valor pago pelo Day Use é abatido da mensalidade.",
  },
];

const COMPARISON = [
  {
    criterion: "Presença do instrutor",
    conventional: "Sob demanda, quando disponível",
    personal: "Exclusiva",
    nook: "Constante, até 3 alunos por instrutor",
  },
  {
    criterion: "Personalização do treino",
    conventional: "Ficha padrão revisada de tempos em tempos",
    personal: "Totalmente individual",
    nook: "Individual e ajustada a cada sessão",
  },
  {
    criterion: "Custo-benefício",
    conventional: "Mensalidade baixa, acompanhamento baixo",
    personal: "Acompanhamento alto, investimento alto",
    nook: "Acompanhamento alto\ncom investimento intermediário",
  },
  {
    criterion: "Ambiente",
    conventional: "Sala cheia, disputa por equipamento",
    personal: "Depende do espaço contratado",
    nook: "Studio reservado e organizado",
  },
  {
    criterion: "Comunidade",
    conventional: "Pouco vínculo",
    personal: "Relação individual",
    nook: "Vínculo real com quem treina ao seu lado",
  },
];

const AUDIENCE = [
  { icon: Sparkles, title: "Quem está começando agora", text: "Você aprende a executar cada movimento com alguém explicando do lado.", image: assistido1.url },
  { icon: RotateCcw, title: "Quem está voltando a treinar", text: "Retomada gradual, respeitando o seu momento e sem pressa.", image: assistido2.url },
  { icon: ShieldCheck, title: "Quem busca segurança", text: "Postura, amplitude e carga corrigidas durante a série.", image: assistido3.url },
  { icon: HeartHandshake, title: "Quem sente falta de acompanhamento", text: "Instrutores presentes do aquecimento à última repetição.", image: assistido4.url },
  { icon: TrendingUp, title: "Quem quer evoluir a performance", text: "Progressão registrada sessão a sessão, com metas claras.", image: assistido5.url },
  { icon: CalendarCheck, title: "Quem valoriza rotina e constância", text: "Horário definido e treino pronto: fica mais fácil não faltar.", image: metodo4.url },
];

const SESSION = [
  { step: "01", title: "Chegada e organização", text: "Você chega, o treino do dia já está definido e o espaço preparado." },
  { step: "02", title: "Orientação", text: "O instrutor explica o objetivo da sessão, a ordem dos exercícios e o que observar." },
  { step: "03", title: "Execução acompanhada", text: "Cada série é feita com o instrutor por perto, ajustando ritmo e postura." },
  { step: "04", title: "Correção e ajuste", text: "Carga, amplitude e técnica corrigidas na hora — não na próxima semana." },
  { step: "05", title: "Registro da evolução", text: "O que foi feito fica registrado para guiar a progressão da próxima sessão." },
];

export const Route = createFileRoute("/metodo-nook")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://nookgym.com.br/metodo-nook" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "index, follow" },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: "https://nookgym.com.br/metodo-nook" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbSchema([
            { name: "Início", url: `${BRAND.site}/` },
            { name: "Método Nook", url: `${BRAND.site}/metodo-nook` },
          ]),
        ),
      },
    ],
  }),
  component: MethodPage,
});

function MethodPage() {
  useEffect(() => {
    track("view_method");
  }, []);

  return (
    <>
      <section className="border-b border-border pb-20 pt-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Breadcrumbs items={[{ label: "Início", to: "/" }, { label: "Método Nook" }]} />
          <div className="mt-10 grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <span className="eyebrow">Método Nook</span>
              <h1 className="display mt-6 text-[2.75rem] leading-[1.12] sm:text-6xl sm:leading-[1.1]">
                Treino personalizado:
                <br />
                <span className="text-nook-green">presença em cada sessão.</span>
              </h1>
              <p className="mt-8 max-w-xl text-base leading-relaxed text-muted-foreground">
                Na Nook, cada instrutor acompanha no máximo três alunos. O treino é orientado,
                corrigido e adaptado durante a sessão — e fica registrado para dar continuidade ao próximo treino.
              </p>
              <div className="mt-10">
                <ActionLink
                  onClick={() => {
                    track("click_experimental", { where: "metodo_hero" });
                    openTrialDialog("metodo_hero");
                  }}
                >
                  Viver o método na prática
                </ActionLink>
              </div>
            </div>
            <div className="lg:col-span-5">
              <ImageCarousel
                images={[
                  { src: metodo2.url, alt: "Instrutor da Nook orientando aluno em exercício no solo" },
                  { src: metodo6.url, alt: "Instrutor da Nook acompanhando aluna em máquina de treino" },
                  { src: metodo3.url, alt: "Instrutor da Nook corrigindo a execução de aluna na polia" },
                  { src: metodo4.url, alt: "Aluna da Nook treinando em máquina articulada" },
                  { src: metodo5.url, alt: "Instrutor da Nook assistindo aluna durante série na polia" },
                ]}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-border py-20">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-4">
            <h2 className="display text-3xl sm:text-4xl">O que é treino personalizado</h2>
          </div>
          <ul className="grid gap-px overflow-hidden rounded-sm border border-border bg-border sm:grid-cols-2 lg:col-span-8">
            {[
              { t: "Presença do instrutor", d: "Do aquecimento à última série,\nsempre acompanhado." },
              { t: "Correção técnica", d: "Ajuste de postura, amplitude e\nritmo durante a execução." },
              { t: "Constância no dia a dia", d: "Horário marcado, treino pronto e\ninstrutores que notam sua falta:\nfica mais fácil manter a rotina." },
              { t: "Organização", d: "Treino planejado; você não perde\ntempo decidindo o que fazer." },
            ].map((i) => (
              <li key={i.t} className="bg-background p-7">
                <Check className="h-4 w-4 text-nook-green" aria-hidden="true" />
                <h3 className="mt-5 text-base font-semibold">{i.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground whitespace-pre-line">{i.d}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="border-b border-border bg-card py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <span className="eyebrow text-nook-green">Formato 3:1</span>
          <h2 className="display mt-5 text-3xl sm:text-4xl">Como funciona o 3:1</h2>

          <div className="mt-6 grid items-start gap-12 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <p className="text-sm leading-relaxed text-muted-foreground">
                Um instrutor, até três alunos, três treinos diferentes acontecendo ao mesmo tempo.
                Não é aula coletiva: é acompanhamento individual em um espaço reservado.
              </p>

              {/* Ratio display */}
              <div className="mt-10 flex items-center justify-between border-y border-border py-8 sm:py-10">
                <div className="flex flex-col items-center gap-1.5">
                  <span className="display text-6xl text-nook-green sm:text-7xl">1</span>
                  <span className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                    Instrutor
                  </span>
                </div>

                <div className="rounded-full border border-white/20 bg-white/5 px-4 py-1.5 sm:px-5 sm:py-2">
                  <span className="text-[0.65rem] font-bold uppercase tracking-[0.15em] text-foreground">
                    PARA
                  </span>
                </div>

                <div className="flex flex-col items-center gap-1.5">
                  <span className="display text-6xl text-foreground sm:text-7xl">3</span>
                  <span className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                    Alunos
                  </span>
                </div>
              </div>

              {/* Feature bullets */}
              <ul className="mt-10 space-y-5 text-sm text-foreground">
                {[
                  "Cada aluno com o seu próprio treino",
                  "Acompanhamento em todas as séries",
                  "Instrutor presente durante a sessão",
                ].map((t) => (
                  <li key={t} className="flex items-center gap-4">
                    <span className="h-2 w-2 shrink-0 rotate-45 bg-nook-green shadow-[0_0_10px] shadow-nook-green/60" />
                    <span className="font-medium">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-6">
              <img
                src={metodoFormato.url}
                alt="Instrutor da Nook orientando aluna em exercício com halteres"
                loading="lazy"
                decoding="async"
                className="aspect-[4/3] w-full rounded-sm object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-card py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <h2 className="display text-3xl sm:text-4xl">Como funciona uma sessão</h2>

          {/* Desktop timeline */}
          <ol className="relative mt-14 hidden md:grid md:grid-cols-5">
            {/* horizontal connector line */}
            <li aria-hidden="true" className="absolute left-0 right-0 top-[1.125rem] h-px bg-border" />

            {SESSION.map((s, idx) => (
              <li key={s.step} className="relative px-4 pt-0 text-center first:pl-0 last:pr-0">
                <span className="relative z-10 mx-auto grid h-9 w-9 place-items-center rounded-full border border-nook-green bg-card text-sm text-nook-green display">
                  {s.step}
                </span>
                <h3 className="eyebrow mt-8 text-[0.7rem]">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
              </li>
            ))}
          </ol>

          {/* Mobile timeline */}
          <ol className="relative mt-12 space-y-10 md:hidden">
            {/* vertical connector line */}
            <li aria-hidden="true" className="absolute left-[1.125rem] top-2 bottom-2 w-px bg-border" />

            {SESSION.map((s) => (
              <li key={s.step} className="relative pl-14">
                <span className="absolute left-0 top-0 z-10 grid h-9 w-9 place-items-center rounded-full border border-nook-green bg-card text-sm text-nook-green display">
                  {s.step}
                </span>
                <h3 className="eyebrow text-[0.7rem]">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="border-b border-border py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <span className="eyebrow">Perfis</span>
          <h2 className="display mt-4 text-3xl sm:text-4xl">Para quem é</h2>
        </div>
        <AudienceCarousel items={AUDIENCE} />
      </section>

      <section className="border-b border-border py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <h2 className="display text-3xl sm:text-4xl">Formatos lado a lado</h2>

          {/* Mobile: comparação empilhada */}
          <ul className="mt-10 space-y-4 md:hidden">
            {COMPARISON.map((row) => (
              <li key={row.criterion} className="overflow-hidden rounded-sm border border-border">
                <div className="border-b border-border bg-card px-5 py-3">
                  <span className="eyebrow">{row.criterion}</span>
                </div>
                <div className="grid grid-cols-1 divide-y divide-border">
                  <div className="px-5 py-4">
                    <span className="text-[0.7rem] uppercase tracking-[0.14em] text-muted-foreground">
                      Academia convencional
                    </span>
                    <p className="mt-1.5 text-sm leading-snug text-muted-foreground">
                      {row.conventional}
                    </p>
                  </div>
                  <div className="border-l-2 border-l-nook-green px-5 py-4">
                    <span className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-nook-green">
                      Método Nook
                    </span>
                    <p className="mt-1.5 text-sm leading-snug text-foreground whitespace-pre-line">{row.nook}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {/* Desktop: tabela */}
          <div className="mt-10 hidden md:block">
            <table className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th scope="col" className="py-4 pr-6 font-normal text-muted-foreground">
                    Academia convencional
                  </th>
                  <th scope="col" className="py-4 font-semibold text-nook-green">
                    Método Nook
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row) => (
                  <tr key={row.criterion} className="border-b border-border align-top">
                    <td className="py-5 pr-6 text-muted-foreground">{row.conventional}</td>
                    <td className="py-5 text-foreground whitespace-pre-line">{row.nook}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="border-b border-border py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <Faq items={FAQ} />
        </div>
      </section>

      <section className="relative overflow-hidden bg-offwhite py-20 text-offwhite-foreground">
        <img
          src={metodoFormato.url}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-20 mix-blend-multiply"
        />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <h2 className="display max-w-2xl text-4xl leading-[1.1] sm:text-5xl">
            Sinta o método
            <br />
            antes de decidir.
          </h2>
          <button
            type="button"
            onClick={() => {
              track("click_experimental", { where: "metodo_cta_final" });
              openTrialDialog("metodo_cta_final");
            }}
            className="mt-9 inline-flex items-center rounded-sm bg-ink px-7 py-4 text-[0.78rem] font-semibold uppercase tracking-[0.14em] text-foreground"
          >
            Garanta seu Day Use →
          </button>
        </div>
      </section>
    </>
  );
}
